import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useBlocker, useNavigate } from 'react-router-dom';
import type { Collection } from '../domain/collections';
import type { DraftFields } from '../domain/drafts';
import type { ListingType } from '../domain/listings';
import { BULK_CREATE_MARK, BulkSelection, MAX_BULK_PHOTOS } from '../domain/bulkSelection';
import type { BulkItem, BulkPhoto, UndoableChange } from '../domain/bulkSelection';
import { useOwnedCollections } from '../hooks/useCollections';
import { useDraftOutbox } from '../hooks/useDraftOutbox';
import { useObjectUrl } from '../hooks/useObjectUrl';
import { formatPrice, LISTING_TYPES, listingTypeLabel } from '../utils/listings';
import { readPhotoSize } from '../utils/photos';
import ConfirmDialog from '../components/ConfirmDialog';
import PhotoThumb from '../components/PhotoThumb';
import { ActionError } from '../components/PageStates';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Check, ImagePlus, Loader2, Merge, MoreHorizontal, PenLine, Trash2 } from 'lucide-react';
import { ErrorText } from '../components/ErrorText';

const DRAFTS_PATH = '/my-listings/drafts';
const UNDO_TOAST_MS = 8000;

type Step = 'pick' | 'defaults' | 'review';

type Defaults = {
  listingType: ListingType;
  collectionId: string;
  free: boolean;
  price: string;
  location: string;
};

const CHANGE_DONE: Record<UndoableChange, (n: number) => string> = {
  group: (n) => `Grouped ${n} photos`,
  merge: () => 'Merged 2 items',
  takeOut: () => 'Took out a photo',
};

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

type FocusTarget =
  { kind: 'menu'; photoId: string } | { kind: 'item'; photoId: string } | { kind: 'list' };

/** A photo to land on once `photoId` is removed: its item-mate, else the next item, else the previous. */
function neighborOf(selection: BulkSelection, photoId: string): string | null {
  const items = selection.items;
  const at = items.findIndex((i) => i.photos.some((p) => p.id === photoId));
  const mate = items[at]?.photos.find((p) => p.id !== photoId);
  if (mate) return mate.id;
  return (items[at + 1] ?? items[at - 1])?.photos[0].id ?? null;
}

/**
 * `/listings/new-from-photos` — up to 20 photos become drafts in one go
 * (021 US1): pick, set the starting values once, arrange photos into items,
 * then "Create N Drafts" saves them in the page and lands on Drafts at once;
 * they sync on their own (FR-002–FR-006). Nothing is kept until Create, so
 * leaving with photos asks first (FR-005a).
 */
export default function NewFromPhotos() {
  const navigate = useNavigate();
  const { outbox } = useDraftOutbox();
  const owned = useOwnedCollections();
  const [step, setStep] = useState<Step>('pick');
  const [selection, setSelection] = useState<BulkSelection | null>(null);
  const [leftOut, setLeftOut] = useState(0);
  const [defaults, setDefaults] = useState<Defaults>({
    listingType: 'sale',
    collectionId: '',
    // A price is optional: left blank, a draft has none, as Free does (016 FR-014).
    free: false,
    price: '',
    location: '',
  });
  const [selecting, setSelecting] = useState(false);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [toast, setToast] = useState<{ text: string; undo: boolean } | null>(null);
  const [toastHeld, setToastHeld] = useState(false);
  /** Where focus goes once an action has re-rendered the items (NFR-005). */
  const [focusTo, setFocusTo] = useState<FocusTarget | null>(null);
  const [error, setError] = useState<unknown>(null);

  const mine = owned.status === 'ready' ? owned.data.collections : null;
  // My default collection starts chosen, once the collections are known.
  useEffect(() => {
    if (!mine || defaults.collectionId) return;
    const start = mine.find((c) => c.isDefault) ?? mine[0];
    if (start) setDefaults((d) => ({ ...d, collectionId: start.id }));
  }, [mine, defaults.collectionId]);

  const photoCount = selection?.photoCount ?? 0;

  // Leaving by Cancel, a link or Back asks first while photos would be lost
  // (FR-005a); Create's own way to Drafts doesn't.
  const leaving = useRef(false);
  const confirmed = useRef(false);
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      !leaving.current && photoCount > 0 && currentLocation.pathname !== nextLocation.pathname,
  );
  useEffect(() => {
    if (blocker.state === 'blocked') confirmed.current = false;
  }, [blocker.state]);

  useEffect(() => {
    if (!focusTo) return;
    const target =
      focusTo.kind === 'menu'
        ? document.querySelector<HTMLElement>(`[data-photo-menu="${focusTo.photoId}"]`)
        : focusTo.kind === 'item'
          ? document.querySelector<HTMLElement>(`[data-photos~="${focusTo.photoId}"]`)
          : document.querySelector<HTMLElement>('[data-bulk-items]');
    target?.focus();
    setFocusTo(null);
  }, [focusTo, selection]);

  // Leaving the page drops picked photos too; the browser asks first.
  useEffect(() => {
    if (photoCount === 0) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [photoCount]);

  const pickPhotos = (files: File[]) => {
    if (files.length === 0) return;
    const picked = files.map((file) => ({ id: crypto.randomUUID(), file }));
    const started = BulkSelection.start(picked);
    setSelection(started);
    setLeftOut(Math.max(0, files.length - MAX_BULK_PHOTOS));
    setStep('defaults');
    // Copy each photo into the page (R7): reading it gives its size.
    for (const { id, file } of picked.slice(0, MAX_BULK_PHOTOS)) {
      readPhotoSize(file).then(
        (size) => setSelection((s) => s?.copied(id, size) ?? s),
        () => setSelection((s) => s?.failed(id) ?? s),
      );
    }
  };

  const change = useCallback((next: (s: BulkSelection) => BulkSelection, toastText?: string) => {
    setSelection((s) => (s ? next(s) : s));
    if (toastText) setToast({ text: toastText, undo: true });
  }, []);

  const undo = useCallback(() => {
    setSelection((s) => (s?.canUndo ? s.undo() : s));
    setToast(null);
    setToastHeld(false);
    // The Undo button goes with the toast; don't leave focus on nothing.
    if (!document.activeElement || document.activeElement === document.body) {
      setFocusTo({ kind: 'list' });
    } else if ((document.activeElement as HTMLElement).dataset.undo !== undefined) {
      setFocusTo({ kind: 'list' });
    }
  }, []);

  // Cmd/Ctrl+Z undoes on the review step, except while typing in a field.
  useEffect(() => {
    if (step !== 'review') return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() !== 'z' || !(event.metaKey || event.ctrlKey) || event.shiftKey)
        return;
      const target = event.target as HTMLElement | null;
      if (target?.closest('input, textarea, select, [contenteditable="true"]')) return;
      event.preventDefault();
      undo();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [step, undo]);

  // The toast stays while it's hovered or focused, so it can be reached.
  useEffect(() => {
    if (!toast || toastHeld) return;
    const timer = setTimeout(() => setToast(null), UNDO_TOAST_MS);
    return () => clearTimeout(timer);
  }, [toast, toastHeld]);

  const cancel = () => navigate('/my-listings');

  const fields = (): DraftFields => ({
    collectionId: defaults.collectionId,
    title: '',
    description: '',
    listingType: defaults.listingType,
    price: defaults.free || !defaults.price ? null : defaults.price,
    location: defaults.location.trim(),
    expiresAt: null,
  });

  const create = () => {
    if (!selection) return;
    const ready = selection.readyItems();
    if (ready.length === 0 || !defaults.collectionId) return;
    performance.mark(BULK_CREATE_MARK);
    try {
      outbox.createDrafts(
        fields(),
        ready.map((item) =>
          item.photos.map((p) => ({ file: p.file, width: p.size!.width, height: p.size!.height })),
        ),
      );
    } catch {
      performance.clearMarks(BULK_CREATE_MARK);
      setError(new Error("Couldn't save these drafts in this page. Nothing was created."));
      return;
    }
    const rest = selection.withoutItems(ready.map((i) => i.id));
    if (rest.photoCount === 0) {
      leaving.current = true;
      navigate(DRAFTS_PATH, { state: { created: ready.length } });
      return;
    }
    setSelection(rest);
    setError(null);
    setToast({
      text: `Created ${plural(ready.length, 'draft', 'drafts')}. The rest are still copying.`,
      undo: false,
    });
  };

  const collectionName = mine?.find((c) => c.id === defaults.collectionId)?.name;
  const noCollection = owned.status === 'error' || (mine !== null && mine.length === 0);

  return (
    <div className="mx-auto max-w-2xl">
      {step === 'pick' && <PickStep onPick={pickPhotos} onCancel={cancel} />}

      {step === 'defaults' && selection && (
        <DefaultsStep
          selection={selection}
          leftOut={leftOut}
          defaults={defaults}
          onChange={(changes) => setDefaults((d) => ({ ...d, ...changes }))}
          collections={mine}
          loading={owned.status === 'loading'}
          noCollection={noCollection}
          onRetry={owned.reload}
          onCancel={cancel}
          onNext={() => setStep('review')}
        />
      )}

      {step === 'review' && selection && (
        <ReviewStep
          selection={selection}
          summary={[
            listingTypeLabel(defaults.listingType),
            defaults.free ? 'Free' : defaults.price ? formatPrice(defaults.price) : null,
            collectionName,
            defaults.location.trim(),
          ]
            .filter(Boolean)
            .join(' · ')}
          selecting={selecting}
          checked={checked}
          toast={toast}
          onToastHeld={setToastHeld}
          error={error}
          canCreate={!noCollection && Boolean(defaults.collectionId)}
          onCancel={cancel}
          onChangeDefaults={() => setStep('defaults')}
          onToggleSelecting={() => {
            setSelecting((on) => !on);
            setChecked(new Set());
          }}
          onToggleChecked={(photoId) =>
            setChecked((c) => {
              const next = new Set(c);
              if (!next.delete(photoId)) next.add(photoId);
              return next;
            })
          }
          onGroup={() => {
            const first = selection.items.flatMap((i) => i.photos).find((p) => checked.has(p.id));
            change((s) => s.group(checked), CHANGE_DONE.group(checked.size));
            if (first) setFocusTo({ kind: 'item', photoId: first.id });
            setSelecting(false);
            setChecked(new Set());
          }}
          onMerge={(itemId) => {
            change((s) => s.mergeWithPrevious(itemId), CHANGE_DONE.merge(2));
            setFocusTo({ kind: 'item', photoId: itemId });
          }}
          onTakeOut={(photoId) => {
            change((s) => s.takeOut(photoId), CHANGE_DONE.takeOut(1));
            setFocusTo({ kind: 'menu', photoId });
          }}
          onRemove={(photoId) => {
            const next = neighborOf(selection, photoId);
            change((s) => s.removePhoto(photoId));
            setToast(null);
            setFocusTo(next ? { kind: 'item', photoId: next } : { kind: 'list' });
          }}
          onUndo={undo}
          onCreate={create}
        />
      )}

      <ConfirmDialog
        open={blocker.state === 'blocked'}
        onOpenChange={(open) => {
          if (!open && !confirmed.current && blocker.state === 'blocked') blocker.reset();
        }}
        title={`Discard ${plural(photoCount, 'photo', 'photos')}?`}
        description={<p>They haven't been made into drafts yet.</p>}
        confirmLabel="Discard"
        onConfirm={() => {
          confirmed.current = true;
          leaving.current = true;
          if (blocker.state === 'blocked') blocker.proceed();
        }}
      />
    </div>
  );
}

// ── Pick ──────────────────────────────────────────────────────────────────

function PickStep({ onPick, onCancel }: { onPick: (files: File[]) => void; onCancel: () => void }) {
  return (
    <div className="space-y-6">
      <h1 className="text-4xl font-bold tracking-tight text-foreground">New from photos</h1>
      <div className="space-y-4 rounded-3xl bg-card p-6">
        <p className="text-muted-foreground">
          Pick up to {MAX_BULK_PHOTOS} photos. Each becomes its own draft, and you can put photos of
          the same thing together next.
        </p>
        <input
          id="bulk-photos"
          type="file"
          accept="image/*"
          multiple
          className="peer sr-only"
          onChange={(e) => onPick(Array.from(e.target.files ?? []))}
        />
        <Label
          htmlFor="bulk-photos"
          className="inline-flex h-11 cursor-pointer items-center gap-2 rounded-full bg-primary px-6 font-semibold text-primary-foreground peer-focus-visible:ring-2 peer-focus-visible:ring-ring"
        >
          <ImagePlus className="size-5" aria-hidden="true" />
          Choose photos
        </Label>
      </div>
      <div className="flex justify-end">
        <Button variant="ghost" className="rounded-full text-primary" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

// ── Starting values ───────────────────────────────────────────────────────

function DefaultsStep({
  selection,
  leftOut,
  defaults,
  onChange,
  collections,
  loading,
  noCollection,
  onRetry,
  onCancel,
  onNext,
}: {
  selection: BulkSelection;
  leftOut: number;
  defaults: Defaults;
  onChange: (changes: Partial<Defaults>) => void;
  collections: Collection[] | null;
  loading: boolean;
  noCollection: boolean;
  onRetry: () => void;
  onCancel: () => void;
  onNext: () => void;
}) {
  const photos = selection.items.flatMap((i) => i.photos);
  return (
    <div className="space-y-6">
      <h1 className="text-4xl font-bold tracking-tight text-foreground">New from photos</h1>

      <section aria-labelledby="picked-heading" className="space-y-3 rounded-3xl bg-card p-6">
        <h2 id="picked-heading" className="font-semibold text-foreground">
          {plural(selection.photoCount, 'photo', 'photos')}
        </h2>
        {leftOut > 0 && (
          <p role="status" className="rounded-2xl bg-secondary p-3 text-sm text-foreground">
            You picked {selection.photoCount + leftOut} photos. The first {MAX_BULK_PHOTOS} are
            here; add the rest in another batch.
          </p>
        )}
        <ul aria-label="Picked photos" className="flex flex-wrap gap-2">
          {photos.map((p, i) => (
            <li key={p.id}>
              <Thumb photo={p} alt={`Photo ${i + 1}`} className="size-16" />
            </li>
          ))}
        </ul>
        <p className="text-sm text-muted-foreground">Each photo becomes its own draft.</p>
      </section>

      <section aria-labelledby="defaults-heading" className="space-y-6 rounded-3xl bg-card p-6">
        <div>
          <h2 id="defaults-heading" className="font-semibold text-foreground">
            Starting values for every draft
          </h2>
          <p className="text-sm text-muted-foreground">
            Set once here. Change any of them later on a single draft.
          </p>
        </div>

        <div>
          <Label id="bulk-type-label">Type</Label>
          <Select
            value={defaults.listingType}
            onValueChange={(v) => onChange({ listingType: v as ListingType })}
          >
            <SelectTrigger aria-labelledby="bulk-type-label" className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LISTING_TYPES.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label id="bulk-collection-label">Collection</Label>
          {noCollection ? (
            <div className="mt-1 flex items-center justify-between gap-3 rounded-2xl bg-secondary p-3 text-sm">
              <span>Connect to choose a collection.</span>
              <Button variant="ghost" size="sm" className="rounded-full" onClick={onRetry}>
                Try again
              </Button>
            </div>
          ) : (
            <Select
              value={defaults.collectionId}
              onValueChange={(v) => onChange({ collectionId: v })}
              disabled={loading}
            >
              <SelectTrigger aria-labelledby="bulk-collection-label" className="mt-1">
                <SelectValue placeholder={loading ? 'Loading…' : 'Choose a collection'} />
              </SelectTrigger>
              <SelectContent>
                {(collections ?? []).map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <Checkbox
              id="bulk-free"
              checked={defaults.free}
              onCheckedChange={(v) => onChange({ free: v === true })}
            />
            <Label htmlFor="bulk-free">Free</Label>
          </div>
          {!defaults.free && (
            <div>
              <Label htmlFor="bulk-price">Price (optional)</Label>
              <InputGroup className="mt-1">
                <InputGroupAddon aria-hidden="true">$</InputGroupAddon>
                <InputGroupInput
                  id="bulk-price"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={defaults.price}
                  onChange={(e) => onChange({ price: e.target.value })}
                />
              </InputGroup>
            </div>
          )}
        </div>

        <div>
          <Label htmlFor="bulk-location">Location</Label>
          <Input
            id="bulk-location"
            value={defaults.location}
            onChange={(e) => onChange({ location: e.target.value })}
            maxLength={100}
            placeholder="Neighborhood or city"
            className="mt-1"
          />
        </div>
      </section>

      <div className="flex justify-end gap-3">
        <Button variant="ghost" className="rounded-full text-primary" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          className="rounded-full"
          onClick={onNext}
          disabled={noCollection || loading || !defaults.collectionId}
        >
          Next
        </Button>
      </div>
    </div>
  );
}

// ── Review: select and group ──────────────────────────────────────────────

function ReviewStep({
  selection,
  summary,
  selecting,
  checked,
  toast,
  onToastHeld,
  error,
  canCreate,
  onCancel,
  onChangeDefaults,
  onToggleSelecting,
  onToggleChecked,
  onGroup,
  onMerge,
  onTakeOut,
  onRemove,
  onUndo,
  onCreate,
}: {
  selection: BulkSelection;
  summary: string;
  selecting: boolean;
  checked: Set<string>;
  toast: { text: string; undo: boolean } | null;
  onToastHeld: (held: boolean) => void;
  error: unknown;
  canCreate: boolean;
  onCancel: () => void;
  onChangeDefaults: () => void;
  onToggleSelecting: () => void;
  onToggleChecked: (photoId: string) => void;
  onGroup: () => void;
  onMerge: (itemId: string) => void;
  onTakeOut: (photoId: string) => void;
  onRemove: (photoId: string) => void;
  onUndo: () => void;
  onCreate: () => void;
}) {
  const items = selection.items;
  const n = selection.createCount;
  const leftOutNotes = [
    selection.copyingCount > 0 &&
      `${plural(selection.copyingCount, 'item', 'items')} still copying`,
    selection.failedCount > 0 &&
      `${selection.failedCount === 1 ? '1 item has' : `${selection.failedCount} items have`} a photo that couldn't be read`,
  ].filter(Boolean);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <Button variant="ghost" className="rounded-full text-primary" onClick={onCancel}>
          Cancel
        </Button>
        <h1 className="text-xl font-semibold text-foreground">
          {selecting ? `${checked.size} selected` : plural(selection.photoCount, 'photo', 'photos')}
        </h1>
        <Button
          variant="ghost"
          className="rounded-full font-semibold text-primary"
          onClick={onToggleSelecting}
        >
          {selecting ? 'Done' : 'Select'}
        </Button>
      </div>

      {selecting ? (
        <p className="px-3 text-sm text-muted-foreground">
          Check the photos that show the same thing, then group them.
        </p>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-2 px-3 text-sm text-muted-foreground">
          <span>{summary}</span>
          <Button
            variant="ghost"
            size="sm"
            className="rounded-full text-primary"
            onClick={onChangeDefaults}
          >
            Change starting values
          </Button>
        </div>
      )}

      {error !== null && <ActionError error={error} />}

      <ul aria-label="Items" data-bulk-items tabIndex={-1} className="space-y-3 outline-none">
        {items.map((item, index) => (
          <ItemRow
            key={item.id}
            item={item}
            index={index}
            label={selection.itemLabel(index)}
            selecting={selecting}
            checked={checked}
            onToggleChecked={onToggleChecked}
            onMerge={onMerge}
            onTakeOut={onTakeOut}
            onRemove={onRemove}
          />
        ))}
      </ul>

      {/* Sticky, not fixed: it rides the bottom of the screen while there are
          items below, and settles under the last one instead of over the footer. */}
      <div className="sticky bottom-0 z-10 -mx-4 bg-gradient-to-t from-background via-background/95 to-transparent px-4 pb-6 pt-10">
        <div className="space-y-3">
          <div
            role="status"
            aria-live="polite"
            onMouseEnter={() => onToastHeld(true)}
            onMouseLeave={() => onToastHeld(false)}
            onFocus={() => onToastHeld(true)}
            onBlur={() => onToastHeld(false)}
          >
            {toast && (
              <div className="flex items-center justify-between gap-3 rounded-full bg-card py-1 pl-5 pr-1 text-sm text-foreground shadow-lg">
                <span>{toast.text}</span>
                {toast.undo && selection.canUndo && (
                  <Button
                    variant="ghost"
                    className="rounded-full font-semibold text-primary"
                    data-undo
                    onClick={onUndo}
                  >
                    Undo
                  </Button>
                )}
              </div>
            )}
          </div>
          {selecting ? (
            <Button
              className="h-12 w-full rounded-full text-base"
              disabled={checked.size < 2}
              onClick={onGroup}
            >
              <Merge aria-hidden="true" />
              Group {checked.size} photos
            </Button>
          ) : (
            <>
              <Button
                className="h-12 w-full rounded-full text-base"
                disabled={n === 0 || !canCreate}
                onClick={onCreate}
              >
                <PenLine aria-hidden="true" />
                Create {n} {n === 1 ? 'Draft' : 'Drafts'}
              </Button>
              {leftOutNotes.length > 0 && (
                <p className="text-center text-sm text-muted-foreground">
                  {leftOutNotes.join(' · ')}
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ItemRow({
  item,
  index,
  label,
  selecting,
  checked,
  onToggleChecked,
  onMerge,
  onTakeOut,
  onRemove,
}: {
  item: BulkItem;
  index: number;
  label: string;
  selecting: boolean;
  checked: Set<string>;
  onToggleChecked: (photoId: string) => void;
  onMerge: (itemId: string) => void;
  onTakeOut: (photoId: string) => void;
  onRemove: (photoId: string) => void;
}) {
  const n = index + 1;
  const acted = useRef(false);
  const multi = item.photos.length > 1;
  return (
    <li
      aria-label={label}
      data-photos={item.photos.map((p) => p.id).join(' ')}
      tabIndex={-1}
      className="space-y-1 rounded-3xl bg-card p-2 outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <p className="px-2 pt-1 text-sm font-semibold text-muted-foreground" aria-hidden="true">
        Item {n} · {plural(item.photos.length, 'photo', 'photos')}
      </p>
      {item.photos.map((photo, j) => {
        const name = `Photo ${j + 1} of item ${n}`;
        const title = `Photo ${j + 1}`;
        if (selecting) {
          const on = checked.has(photo.id);
          // The whole row is the checkbox (NFR-005); its name starts with the words it shows.
          return (
            <button
              key={photo.id}
              type="button"
              role="checkbox"
              aria-checked={on}
              aria-label={name}
              onClick={() => onToggleChecked(photo.id)}
              className="flex w-full items-center gap-3 rounded-2xl p-2 text-left outline-none transition-colors hover:bg-secondary focus-visible:ring-2 focus-visible:ring-ring aria-checked:bg-secondary"
            >
              <span
                aria-hidden="true"
                className={
                  on
                    ? 'flex size-6 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground'
                    : 'size-6 shrink-0 rounded-full ring-2 ring-inset ring-muted-foreground/40'
                }
              >
                {on && <Check className="size-4" />}
              </span>
              <Thumb photo={photo} />
              <span className="font-medium text-foreground">{title}</span>
            </button>
          );
        }
        return (
          <div key={photo.id} className="flex items-center gap-3 rounded-2xl p-2">
            <Thumb photo={photo} />
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="font-medium text-foreground">{title}</span>
              {photo.failed ? (
                <ErrorText className="text-sm font-medium">Couldn&apos;t read this photo</ErrorText>
              ) : photo.size === null ? (
                <span className="flex items-center gap-1 text-sm text-muted-foreground">
                  <Loader2 className="size-3 animate-spin" aria-hidden="true" />
                  Copying…
                </span>
              ) : null}
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="shrink-0 rounded-full text-primary"
                  aria-label={`More for ${name.toLowerCase()}`}
                  data-photo-menu={photo.id}
                >
                  <MoreHorizontal />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="rounded-2xl border-none shadow-lg"
                // After an action, focus moves where the action left things;
                // closed without one, it goes back to the button as usual.
                onCloseAutoFocus={(e) => {
                  if (acted.current) e.preventDefault();
                  acted.current = false;
                }}
              >
                {multi && (
                  <DropdownMenuItem
                    onSelect={() => {
                      acted.current = true;
                      onTakeOut(photo.id);
                    }}
                  >
                    Take out
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  className="text-destructive"
                  onSelect={() => {
                    acted.current = true;
                    onRemove(photo.id);
                  }}
                >
                  <Trash2 aria-hidden="true" />
                  Remove photo
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      })}
      {index > 0 && !selecting && (
        <Button
          variant="ghost"
          size="sm"
          className="rounded-full text-primary"
          // Starts with the words it shows (WCAG 2.5.3), then says which items.
          aria-label={`Merge with previous (item ${n} into item ${n - 1})`}
          onClick={() => onMerge(item.id)}
        >
          <Merge aria-hidden="true" />
          Merge with previous
        </Button>
      )}
    </li>
  );
}

/** A picked photo, small. In a row whose text says which it is, it needs no `alt`. */
function Thumb({
  photo,
  alt,
  className = 'size-14',
}: {
  photo: BulkPhoto;
  alt?: string;
  className?: string;
}) {
  const url = useObjectUrl(photo.failed ? null : photo.file);
  return <PhotoThumb url={url} alt={alt} className={className} />;
}
